
import * as TX from 'app/calcs/Accountant/Transactions/interfaces'
import { ExpenseMap, personalExpenseFieldNames } from 'app/calcs/expenses/interfaces'
import { AbstractWhen, getDateOf } from 'app/calcs/predicates';
import { AutoOwnership, deadBy, getEstimatedExpenseSwitchoverDate, IAutoExpense, IRetirementPerson, isValidPerson, missingPeopleProperties, missingPersonProperties, Profile, retirementDate, VerifiablePersonProperties } from 'app/redux/Profile/model'
import * as utils from 'app/utils'
import { isAlive } from 'app/utils/DateCalcs'
import * as _ from 'lodash'
import * as moment from 'moment'

type ExpenseGroups = 'home' | 'auto' | 'food' | 'clothingAndPersonal' | 'entertainment' | 'other'
type ExpenseGroupValues = utils.HashedNumbers<ExpenseGroups>
type ExpenseGroupMap = utils.HashedThings<ExpenseGroups, ExpenseMap>
const expenseGroups: ExpenseGroupMap = {
  home: ['mortgage', 'propertyTaxes', 'homeInsurance', 'homeMaintenance', 'homeUtilities'],
  auto: ['auto'],
  food: ['groceries', 'diningOut'],
  clothingAndPersonal: ['clothing', 'grooming'],
  entertainment: ['travel', 'entertainment'],
  other: ['medical', 'charity', 'taxes', 'savingsAndDebt']
}

type ExpenseClassifications = 'fixed' | 'discretionary'
type ExpenseClassificationValues = utils.HashedNumbers<ExpenseClassifications>
type ExpenseClassificationMap = utils.HashedThings<ExpenseClassifications, ExpenseMap>
const expenseClassifications: ExpenseClassificationMap = {
  fixed: ['mortgage', 'propertyTaxes', 'homeInsurance', 'homeMaintenance', 'homeUtilities',
    'auto', 'groceries', 'clothing', 'grooming', 'taxes', 'savingsAndDebt'
  ],
  discretionary: ['diningOut', 'travel', 'entertainment', 'charity']
}

// these are the fields in the expenses.auto hash that drive expense totals
const autoExpenseFieldNames = ['autoMaintenance', 'fuelCosts', 'insurance', 'loanPayments']

type AllExpenses = ExpenseGroups | ExpenseClassifications
type AllExpenseMaps = ExpenseGroupMap | ExpenseClassificationMap
type AllExpenseValues = ExpenseGroupValues | ExpenseClassificationValues

interface IExpenseState {
  value: number
  kind: string
}

interface IExpenseSummary {
  results: IExpenseState[]
}

// these fields are for the family
const familyExpenseFieldNames: ExpenseMap = ['auto', 'mortgage', 'charity', 'propertyTaxes', 'taxes', 'savingsAndDebt',
  'homeInsurance', 'homeMaintenance', 'homeUtilities', 'groceries', 'diningOut',
  'travel', 'entertainment'
]
const expenseSource = 'Expenses'

/**
 * Calculates the applicable auto expenses for one car.
 *
 * @param auto - the individual auto expenses hash
 */
const calcAutoExpense = (auto: IAutoExpense) => _.sum(_.values(_.pick(auto, autoExpenseFieldNames)))

class ExpenseCalculations implements TX.ITransactionGenerator {

  constructor(protected profile: Profile) { }

  getExpensesAtDeath(person: IRetirementPerson): TX.ITransactionItem[] {
    const q = getDateOf(AbstractWhen.personDeath, this.profile, person)
    return getExpenseSummaryAtDate(this.profile, q.when)
  }

  getInitialExpenseTransaction(when: moment.Moment): TX.ITransactionItem[] {
    return getExpenseSummaryAtDate(this.profile, when)
  }

  getTimeLineTransactions(): Promise<TX.ITransactionItem[]> {
    const r: TX.ITransactionItem[] = []
    if (!this.profile.expenses) { return Promise.resolve(r) }

    // if the user has opted to [only] enter an estimated monthly amount, handle that now
    let estimatedMonthlyExpenses = getEstimatedMonthlyExpenses(this.profile, null)
    if (estimatedMonthlyExpenses) {
      estimatedMonthlyExpenses = this.profile.expenses.estimatedMonthlyExpensesPostSwitchover
      const firstRetired = this.profile.getFirstRetirement()
      const firstRetirementDate = retirementDate(firstRetired)
      const switchoverDate = getEstimatedExpenseSwitchoverDate(this.profile)
      /*
      if (firstRetirementDate.isBefore(switchoverDate)) {
        r.push({
          when: firstRetirementDate,
          what: { estimatedMonthlyExpenses },
          description: 'Your estimated monthly expenses are <a>' + utils.toCurrency(estimatedMonthlyExpenses) + '</a>'
        })
      }
      */
      r.push({
        when: switchoverDate,
        section: 'Expenses',
        source: expenseSource,
        who: firstRetired.firstName,
        what: { estimatedMonthlyExpenses },
        description: 'Your estimated monthly expenses are now <a>' + utils.toCurrency(estimatedMonthlyExpenses) + '</a>'
      })
      return Promise.resolve(r)
    }

    const ddd = []

    const spouse = this.profile.getSpouse()
    if (spouse && isValidPerson(spouse) && spouse.selectedRetirementAge) {
      ddd.push(getDateOf(AbstractWhen.firstPersonDying, this.profile))
    }
    const eee = _.uniqBy(ddd, (q) => utils.fractionalYearAndMonth(q.when))
    // tslint:disable-next-line:no-console
    console.log('eee: ' + JSON.stringify(eee))
    const sss = _.orderBy(eee, (q) => q.when.unix())

    const x3 = _.reduce(sss, (acc, item) => {
      acc.push(...getExpenseSummaryAtDate(this.profile, item.when))
      return acc
    }, [])
    const x4 = _.reduce(x3, (acc, item) => {
      if (acc.a !== item.what.expenses) {
        acc.txs.push(item)
        acc.a = item.what.expenses
      }
      return acc
    }, { a: 0, txs: [] })
    // tslint:disable-next-line:no-console
    console.log('x4: ' + JSON.stringify(x4, undefined, 4))

    return Promise.resolve(x4.txs)
  }

  getTransactions(when: moment.Moment, tolerance: TX.TransactionTolerance = 'month'): Promise<TX.ITransactionItem[]> {
    const r: TX.ITransactionItem[] = []

    // if the user has selected to only enter a single estimated monthly amount, handle that now
    const estimatedMonthlyExpenses = getEstimatedMonthlyExpenses(this.profile, when)
    if (estimatedMonthlyExpenses) {
      r.push({
        when,
        section: 'Expenses',
        source: expenseSource,
        who: '',
        what: { estimatedMonthlyExpenses: -estimatedMonthlyExpenses },
        description: '(estimated) monthly expenses'
      })
      return Promise.resolve(r)
    }

    return Promise.resolve(getAllExpenses(this.profile, when)
      .map((item): TX.ITransactionItem => {
        return {
          when,
          section: 'Expenses',
          source: expenseSource,
          who: '',
          what: { [item.kind]: -item.value },
          description: 'monthly expenses: ' + item.kind
        }
      }))

    /* F&F want the detailed expenses
    when = when.clone()
    _(getAllExpensesGrouped<ExpenseClassificationValues>(this.profile, when, expenseClassifications))
      .forEach((item, key) => {
        // expenses are all on an annual basis. Correct if needed
        let amount = item / 12
        if (amount > 0) { amount = -amount }
        if (0 !== amount) {
          r.push({
            when,
            what: { [key]: amount },
            description: key + ' expenses'
          })
        }
      })

    return Promise.resolve(r)
    */
  }
}

function getAfterRetirementValue(metric): number {
  return _.has(metric, 'afterRetirement') ? metric.afterRetirement * getMultiplier(metric.afterRetirementUnits) : getBeforeRetirementValue(metric)
}

function getBeforeRetirementValue(metric): number {
  return _.has(metric, 'beforeRetirement') ? metric.beforeRetirement * getMultiplier(metric.beforeRetirementUnits) : 0
}

function getAllExpenses(profile: Profile, when: moment.Moment): IExpenseState[] {

  const r: IExpenseState[] = []

  // check if an auto expense applies at this time
  const autoExpenseApplies = (auto: IAutoExpense) => {
    switch (auto.ownership) {
      case AutoOwnership.Joint:
        return true // assumes that since we were called, someone is alive

      case AutoOwnership.Member:
        return isAlive(profile.getHeadOfHousehold(), when)

      case AutoOwnership.Spouse:
        return isAlive(profile.getSpouse(), when)

      default:
        return false
    }
  }

  const estimatedMonthlyExpenses = getEstimatedMonthlyExpenses(profile, when)
  if (estimatedMonthlyExpenses) {
    r.push({ kind: 'estimatedMonthlyExpenses', value: estimatedMonthlyExpenses })
    return r
  }

  if (!profile.expenses) { return r }

  const switchoverDate = getDateOf(AbstractWhen.expenseSwitchover, profile).when

  // handle the family expenses. These changeover:
  //  - at the expenses.expenseSwitchoverAge of the member
  _(_.intersection(familyExpenseFieldNames, _.keysIn(profile.expenses)))
    .forEach((fieldName) => {
      // auto expenses are expressed in an array of cars...
      if (fieldName === 'auto') {
        const value = _(profile.expenses.auto).filter(autoExpenseApplies).sumBy(calcAutoExpense)
        r.push({ kind: fieldName, value })
      } else {
        r.push({ kind: fieldName, value: getSingleExpenses(profile.expenses[fieldName], when, switchoverDate) })
      }
    })

  // handle the personal expenses. These changeover:
  //  - at the expenses.expenseSwitchoverAge of the member
  _(_.intersection(personalExpenseFieldNames, _.keysIn(profile.expenses)))
    .forEach((fieldName) => {
      const people = profile.expenses[fieldName]
      _.forOwn(people, (metric, personId) => {
        const person = profile.getMember(personId)
        if (!isAlive(person, when)) { return }
        if (isValidPerson(person)) {
          r.push({ kind: fieldName, value: getSingleExpenses(metric, when, switchoverDate) })
        } else {
          // tslint:disable-next-line:no-console
          console.error('Profile expenses references unknown member or no DOB: ' + personId)
          profile.cleanUpPerson(person, false)
        }
      })

    })

  return r
}

/**
 * Returns a monthly expense number of estimated monthly expenses have been selected.
 *
 * @param profile - the users profile
 * @param when - the date for which the expenses are needed
 */
function getEstimatedMonthlyExpenses(profile: Profile, when: moment.Moment) {
  if (profile.expenses && profile.expenses.estimatedMonthlyExpenses) {
    // if the caller wasn't specific about when, just return the pre-switchover expense amount
    if (!when) { return profile.expenses.estimatedMonthlyExpenses }

    return (when.isBefore(getEstimatedExpenseSwitchoverDate(profile))) ? profile.expenses.estimatedMonthlyExpenses : profile.expenses.estimatedMonthlyExpensesPostSwitchover
  } else {
    return 0
  }
}

function getExpensesAtSwitchoverAge(profile: Profile): TX.ITransactionItem[] {
  if (!profile.expenses) { return [] }
  const q = getDateOf(AbstractWhen.expenseSwitchover, profile)
  return getExpenseSummaryAtDate(profile, q.when)
}

function getExpenseSummaryAtDate(profile: Profile, when: moment.Moment): TX.ITransactionItem[] {
  const expenses = getAllExpenses(profile, when)
  const amount = _(expenses).sumBy((x) => x.value)
  const r: TX.ITransactionItem[] = []
  if (0 !== amount) {
    r.push({
      when,
      section: 'Expenses',
      source: expenseSource,
      who: '',
      what: {
        expenses: amount
      },
      description: 'Expenses are: <a>' + utils.toCurrency(amount) + '</a>'
    })
  }
  return r
}

function getAllExpensesGrouped<V extends AllExpenseValues>(profile: Profile, when: moment.Moment, map: AllExpenseMaps = expenseGroups): V {
  const expenses = getAllExpenses(profile, when)
  // tslint:disable-next-line:no-object-literal-type-assertion
  const r = {} as V
  _(map)
    .keysIn()
    .forEach((groupName): V => {
      const fields = map[groupName]
      r[groupName] = _(fields).sumBy((fn) => _(expenses).filter((item) => item.kind === fn).sumBy((v) => v.value))
      return r
    })
  return r
}

/**
 * Compute a single expense.
 *
 * @param metric Returns monthly expense amount.
 * @param when The date to return the expenses for (only used for before/after retirement)
 * @param cutoverWhen - The retirement date
 */
function getSingleExpenses(metric, when: moment.Moment, cutoverWhen: moment.Moment): number {
  const a = utils.fractionalYearAndMonth(when)
  const b = utils.fractionalYearAndMonth(cutoverWhen)
  const beforeRetirement = a < b
  return (beforeRetirement ? getBeforeRetirementValue(metric) : getAfterRetirementValue(metric)) / 12
}

function getMultiplier(unitsName: string): number {
  const multipliers = {
    yearly: 1,
    monthly: 12,
    biWeekly: 365.25 / 7 / 2,
    weekly: 365.25 / 7
  }
  if (!unitsName) { unitsName = 'monthly' }
  if (_.has(multipliers, unitsName)) {
    return multipliers[unitsName]
  }
  throw new Error('Unknown units name found in profile: ' + unitsName)
}

const neededPropertiesForExpenses: VerifiablePersonProperties[] = ['dob', 'selectedAgeExpectancy']

function missingPersonInfoForExpenseCalculation(person: IRetirementPerson): string[] {
  return missingPersonProperties(person, neededPropertiesForExpenses)
}

function missingPeopleInfoForExpenseCalculation(people: IRetirementPerson[]): string[] {
  return missingPeopleProperties(people, neededPropertiesForExpenses)
}

function haveEnoughDataForExpensesCalculation(people: IRetirementPerson[]): boolean {
  const missingInfos = missingPeopleInfoForExpenseCalculation(people)
  return 0 === missingInfos.length
}

/**
 * Produce a simple summary of before and after retirement. The afterRetirement date is the date after the last person retires
 *
 * @param z
 */
function summarize1(profile: Profile): IExpenseSummary {
  const UNITS = 'Units'

  const brExpenses: IExpenseState = {
    value: 0,
    kind: 'before'
  }

  const arExpenses: IExpenseState = {
    value: 0,
    kind: 'after'
  }

  function summer1(z: any) {
    const brText = 'beforeRetirement'
    let carry: number = 0
    if (_.has(z, brText)) {
      const brVal = carry = z[brText] * getMultiplier(z[brText + UNITS])
      brExpenses.value += brVal
    }

    const arText = 'afterRetirement'
    if (_.has(z, arText)) {
      const arVal = z[arText] * getMultiplier(z[arText + UNITS])
      arExpenses.value += arVal
    } else {
      // if there wasn't an explicit afterRetirement, use any beforeRetirement defined
      arExpenses.value += carry
    }

  }

  _(_.intersection(familyExpenseFieldNames, _.keysIn(profile.expenses)))
    .forEach((d) => summer1(profile.expenses[d]))

  _(_.intersection(personalExpenseFieldNames, _.keysIn(profile.expenses)))
    .forEach((d) => {
      const members = profile.expenses[d]
      _.forOwn(members, (memberInfo) => {
        summer1(memberInfo)
      })

    })

  return {
    results: [brExpenses, arExpenses]
  }
}

export {
  calcAutoExpense,
  autoExpenseFieldNames,
  personalExpenseFieldNames,
  getAllExpenses,
  getAllExpensesGrouped,
  getEstimatedExpenseSwitchoverDate,
  getExpensesAtSwitchoverAge,
  getExpenseSummaryAtDate,
  haveEnoughDataForExpensesCalculation,
  missingPersonInfoForExpenseCalculation,
  missingPeopleInfoForExpenseCalculation,
  neededPropertiesForExpenses,
  summarize1,
  IExpenseState,
  IExpenseSummary,

  AllExpenses,
  AllExpenseValues,

  ExpenseClassifications,
  ExpenseClassificationMap,
  ExpenseClassificationValues,

  ExpenseGroups,
  ExpenseGroupValues,
  ExpenseGroupMap,

  ExpenseCalculations,

}



// WEBPACK FOOTER //
// ./src/app/calcs/expenses/index.ts